import { useCallback, useEffect, useRef, useState } from 'react';
import { IconAlertCircle } from '@tabler/icons';
import { ProvinceDialog } from '#root/components';
import { Button, Card } from '#root/components/ui';
import { Skeleton } from '#root/components/ui/primitives';
import { SparksIcon } from '#root/icons';
import { travelerService } from '#root/services';
import type { MatchEligibilityDTO, MatchFeedDTO, MatchGroupDTO } from '#root/models/PrepaidMatchDTO';
import MatchGroupCard from './MatchGroupCard';
import MatchWantDialog from './MatchWantDialog';
import MyReservationCard from './MyReservationCard';
import styles from './match.module.scss';

interface MatchFeedProps {
  /** Pasa a la pestaña "Todos" */
  onGoToAll: () => void;
}

const noticeText = (eligibility: MatchEligibilityDTO) => {
  switch (eligibility.reason) {
    case 'not_capable':
      return 'Completa tu capacitación para pedir estos pedidos';
    case 'no_trip':
      return 'Activa un viaje para pedir estos pedidos';
    case 'not_enough': {
      const missing = Math.max(eligibility.required_offers - eligibility.offers_count, 0);
      return `Te faltan ${missing} ${missing === 1 ? 'oferta' : 'ofertas'} en tu viaje para pedir estos pedidos`;
    }
    default:
      return 'Aún no cumples los requisitos para pedir estos pedidos';
  }
};

const MatchFeed = ({ onGoToAll }: MatchFeedProps) => {
  const [feed, setFeed] = useState<MatchFeedDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // El pedido se conserva al cerrar: el Modal sigue montado para animar la salida
  const [selected, setSelected] = useState<MatchGroupDTO | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Un solo modal de Envío Nacional para todas las cards
  const [provinceOpen, setProvinceOpen] = useState(false);
  const lastRequest = useRef(0);

  const loadFeed = useCallback(async () => {
    const requestId = ++lastRequest.current;
    const res = await travelerService.matchFeed();
    // Solo cuenta la respuesta más reciente (y nada tras desmontar: ver cleanup)
    if (requestId !== lastRequest.current) return;
    if (res.error) {
      setError(res.message ?? 'No se pudieron obtener los Pedidos Match.');
    } else {
      setFeed(res.data as MatchFeedDTO);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadFeed();
    // Refetch silencioso al volver a la pestaña del navegador (sin refresco periódico)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadFeed();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      lastRequest.current++;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadFeed]);

  const handleRetry = () => {
    setLoading(true);
    loadFeed();
  };

  const handleWant = (group: MatchGroupDTO) => {
    setSelected(group);
    setDialogOpen(true);
  };

  // Tras un intento de reservar (éxito o error del BE) el feed se pone al día al cerrar
  const handleDialogClose = (changed: boolean) => {
    setDialogOpen(false);
    if (changed) loadFeed();
  };

  // Desde la franja se abre el checklist sin pedido elegido
  const handleUnlock = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  if (!feed) {
    if (loading) {
      return (
        <div className={styles.feed}>
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} variant="rect" height={220} />
          ))}
        </div>
      );
    }
    return (
      <Card noHover className={`${styles.empty} ${styles.emptyError}`}>
        <IconAlertCircle size={32} />
        <span>{error}</span>
        <Button variant="outline" onClick={handleRetry}>
          Reintentar
        </Button>
      </Card>
    );
  }

  const { eligibility, server_time: serverTime } = feed;
  // Los pedidos con `availability: 'mine'` ya salen en "Tus reservas": no se repiten en la lista
  const groups = feed.groups.filter((group) => group.availability !== 'mine');
  // Un BE sin G3 no manda `my_reservations`
  const reservations = feed.my_reservations ?? [];

  return (
    <div className={styles.feed}>
      {/* Siempre que haya, aunque el viajero ya no sea elegible */}
      {reservations.length > 0 && (
        <section className={styles.reservations} aria-label="Tus reservas">
          <h2 className={styles.sectionTitle}>Tus reservas</h2>
          {reservations.map((reservation) => (
            <MyReservationCard
              key={reservation.id}
              reservation={reservation}
              serverTime={serverTime}
              onChanged={loadFeed}
            />
          ))}
        </section>
      )}
      {!eligibility.eligible && (
        <Card noHover className={styles.notice}>
          <SparksIcon fontSize="small" />
          <span className={styles.noticeText}>{noticeText(eligibility)}</span>
          <Button variant="link" size="sm" onClick={handleUnlock}>
            Ver cómo desbloquear
          </Button>
        </Card>
      )}
      {groups.length === 0 ? (
        <Card noHover className={styles.empty}>
          <SparksIcon fontSize="large" />
          <span>No hay Pedidos Match por ahora</span>
        </Card>
      ) : (
        groups.map((group) => (
          <MatchGroupCard
            key={group.group_key}
            group={group}
            eligible={eligibility.eligible}
            serverTime={serverTime}
            onWant={handleWant}
            onShippingInfo={() => setProvinceOpen(true)}
          />
        ))
      )}
      <ProvinceDialog open={provinceOpen} onClose={() => setProvinceOpen(false)} />
      {(selected || !eligibility.eligible) && (
        <MatchWantDialog
          open={dialogOpen}
          group={selected}
          eligibility={eligibility}
          serverTime={serverTime}
          onClose={handleDialogClose}
          onGoToAll={onGoToAll}
        />
      )}
    </div>
  );
};

export default MatchFeed;
